/**
 * Deriving initials from a name.
 *
 * Separate from `OwnerAvatar.tsx` so that file exports only a component, and
 * because name handling deserves to be testable on its own.
 */

/**
 * The first user-perceived character of a string.
 *
 * `Intl.Segmenter`, not `str[0]` or a spread: an accented letter can be a base
 * plus a combining mark, and an emoji can be several code points joined by
 * zero-width joiners. Both render as one character and both would be cut in
 * half by any code-unit or code-point approach.
 */
function firstGrapheme(value: string): string {
  if (value === '') return '';

  const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  /* Destructured with a default rather than `segment?.segment ?? ''`: the
     empty-string guard above means a non-empty string always yields at least
     one segment, so the optional chain would be unreachable. */
  const [{ segment = '' } = {}] = segmenter.segment(value);

  return segment;
}

/**
 * Up to two initials from a name.
 *
 * First and last word, so "Maria de Sousa Ferreira" gives MF rather than MD.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/u).filter(Boolean);
  if (words.length === 0) return '';

  /* A single-word name yields one initial, not the same letter twice — so the
     tail is taken only when there IS a distinct last word. Destructured with
     defaults rather than indexed with `??`, which would add branches the
     length guard above makes unreachable. */
  const [first = ''] = words;
  const { [words.length - 1]: lastWord = '' } = words;
  const last = words.length > 1 ? lastWord : '';

  return (firstGrapheme(first) + firstGrapheme(last)).toUpperCase();
}
